import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getSessionItemWithExpiry, setSessionItemWithExpiry } from "@/util/MiscUtils";

const CONCIERGE_MODE_KEY = "concierge-mode-enabled";
const CONCIERGE_MODE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface ConciergeModeContextType {
  isConciergeMode: boolean;
  toggleConciergeMode: () => void;
  setConciergeMode: (enabled: boolean) => void;
}

const ConciergeModeContext = createContext<ConciergeModeContextType | null>(null);

export const useConciergeMode = () => {
  const context = useContext(ConciergeModeContext);
  if (!context) {
    throw new Error("useConciergeMode must be used within a ConciergeModeProvider");
  }
  return context;
};

export const ConciergeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize from sessionStorage
  const [isConciergeMode, setIsConciergeMode] = useState<boolean>(() => {
    const stored = getSessionItemWithExpiry(CONCIERGE_MODE_KEY);
    return stored === true;
  });

  // Update sessionStorage when state changes
  useEffect(() => {
    setSessionItemWithExpiry(CONCIERGE_MODE_KEY, isConciergeMode, CONCIERGE_MODE_TTL);
  }, [isConciergeMode]);

  const toggleConciergeMode = useCallback(() => {
    setIsConciergeMode((prev) => !prev);
  }, []);

  const setConciergeMode = useCallback((enabled: boolean) => {
    setIsConciergeMode(enabled);
  }, []);

  return (
    <ConciergeModeContext.Provider value={{ isConciergeMode, toggleConciergeMode, setConciergeMode }}>
      {children}
    </ConciergeModeContext.Provider>
  );
};
